import { useEffect, useState } from "react";

/**
 * "How to Guide" for the Default Ads screen: a Help button (rendered before the
 * search field) that opens a right-side offcanvas explaining how selection,
 * search, and authoring work. Self-contained — owns its open state, Escape-to-close,
 * and a click-away backdrop.
 */
export function HelpGuide() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className="help-btn"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="How to guide"
      >
        <HelpIcon />
        <span>Help</span>
      </button>

      {open && <div className="offcanvas-backdrop" onClick={() => setOpen(false)} aria-hidden />}

      <aside
        className={`offcanvas${open ? " open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="How to guide"
        aria-hidden={!open}
      >
        <header className="offcanvas-head">
          <h2>How to Guide</h2>
          <button type="button" className="offcanvas-close" onClick={() => setOpen(false)} aria-label="Close help">
            ×
          </button>
        </header>

        <div className="offcanvas-body">
          <p className="offcanvas-lead">
            This screen lists the organisations you can publish <strong>Default Ads</strong> to — the ad shown on a
            page position when no paid ad is available. Here's how to use it.
          </p>

          <h3>Open an organisation</h3>
          <p>Click an organisation's name to view and manage its Default Ads.</p>

          <h3>Select organisations</h3>
          <ul>
            <li>
              <strong>Tick the checkbox</strong> next to one or more organisations, then choose a product type from the
              blue <strong>“All Product Types”</strong> button to author a Default Ad for every selected org at once.
            </li>
            <li>
              <strong>Select Visible</strong> — selects only the organisations on the <em>current page</em>.
            </li>
            <li>
              <strong>Select All</strong> — selects <em>every</em> organisation that matches your current search, across
              <em> all pages</em> (not just the one you're viewing).
            </li>
          </ul>

          <h3>Search</h3>
          <p>
            Type in <strong>Search Organisation</strong> to filter by name or customer id. Selecting “Select All” while a
            search is active targets only the matching organisations.
          </p>

          <h3>Default Ads count</h3>
          <p>
            The <strong>“Default Ads : N”</strong> pill on each row shows how many Default Ads that organisation currently
            has.
          </p>

          <h3>Which organisations appear</h3>
          <p>
            Only <strong>active</strong>, ads-enabled organisations that are set up for advertising are listed —
            advertiser accounts and organisations not configured for ads are hidden.
          </p>

          <hr className="offcanvas-rule" />

          <h3>The “New default ad” screen</h3>
          <p>
            You reach this by selecting organisation(s) and a product type, or by opening an organisation and choosing
            <strong> New</strong>. A banner at the top shows how many organisations the ad will be created for — the same
            ad is published to all of them. Fill in:
          </p>
          <ul>
            <li>
              <strong>Advertisement title</strong> — a name to identify the ad in the list.
            </li>
            <li>
              <strong>Position</strong> — <strong>First</strong> shows the ad <em>above</em> paid ads in that position;
              <strong> Last</strong> shows it <em>below</em> them. If another default ad is already First, the new one
              goes on top and the older one moves down.
            </li>
            <li>
              <strong>Start / End date</strong> — the date range the ad is eligible to appear.
            </li>
            <li>
              <strong>Status</strong> — <strong>Active</strong> to run it, <strong>Inactive</strong> to keep it as a
              draft without publishing.
            </li>
            <li>
              <strong>Product type</strong> — Newsletter, Event, Survey, or All. (Locked if you already chose it from the
              “All Product Types” button.)
            </li>
            <li>
              <strong>Link type &amp; value</strong> — where the ad clicks through to: a <strong>Web link</strong> (URL)
              or an <strong>Email</strong> address.
            </li>
            <li>
              <strong>Display on public article pages</strong> — also show the ad on article pages. Available for
              Newsletter / All only.
            </li>
            <li>
              <strong>Image</strong> — upload the ad artwork (PNG, JPG, or GIF). It's resized to a 450px width (GIFs are
              kept as-is).
            </li>
          </ul>
          <p>
            Choose <strong>Create default ad</strong> to save it for every targeted organisation, or
            <strong> Cancel</strong> to go back. Editing an existing ad works the same way, and leaving the image
            untouched keeps the current one.
          </p>
        </div>
      </aside>
    </>
  );
}

function HelpIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.2 2.4c-.8.3-1.2.9-1.2 1.6v.5" />
      <line x1="12" y1="17" x2="12" y2="17" strokeWidth="2.5" />
    </svg>
  );
}
